import React from 'react';
import useIntls from '@opm/hooks/useIntls';
import RenderGameIcon from '@op/components/RenderImg/renderGameIcon';
import type { TGameMap } from '@opm/pages/CompetitorAdComparison/hooks/useGameMap';
import { ITrendItemBase } from '../../types';
import { TTopicTypeColorMap } from '../../utils';
import TrendTopicTags from '../../components/TrendTopicTags';
import { useGameMeta } from '../../components/useGameMeta';
import TrendInfoKpiRow from './TrendInfoKpiRow';
import { DBO_V } from '@op/utils/format';

export interface ITrendInfoKPIRowConfig {
  /** record 上 KPIValue 的字段名 */
  dataKey: string;
  /** 行 label（用于 intl(labelKey)） */
  labelKey: string;
  /** 数值格式化小数位数 */
  decimal?: number;
}

/** CreativeTrends 默认 KPI 行配置 */
const DEFAULT_KPI_ROWS: ITrendInfoKPIRowConfig[] = [
  { dataKey: 'cumulative_impressions', labelKey: 'Impressions' },
  { dataKey: 'creatives', labelKey: 'Ads-1' },
  { dataKey: 'avg_days_active', labelKey: 'Avg. Days Active', decimal: 1 },
];

interface ITrendInfoRowsProps {
  record: ITrendItemBase | null;
  /** 游戏元信息字典（cond.materials_config_games）：id → meta */
  gameMap?: TGameMap;
  /** KPI 行配置（默认 CreativeTrends 三件套），UGC 等扩展页可注入自有字段 */
  kpiConfigs?: ITrendInfoKPIRowConfig[];
  /** Topic Type chip 颜色映射（按 L1 parent 取色，L2 共享 parent 色） */
  topicTypeColorMap?: TTopicTypeColorMap;
}

/**
 * Drawer Trend Info 区（Figma 3902_62017 Section 1）
 *
 * label-value 列表：
 *  - Game        → logo + 游戏名
 *  - Total Views → 带 DoD 环比
 *  - Creatives   → 带 DoD 环比
 *  - Avg. Days Active → 带 DoD 环比
 *  - Type & Elements  → 多色 chip 标签
 */
export default function TrendInfoRows({
  record,
  gameMap,
  kpiConfigs = DEFAULT_KPI_ROWS,
  topicTypeColorMap,
}: ITrendInfoRowsProps) {
  const { intl } = useIntls();
  const { gameName, gameLogo } = useGameMeta(record, gameMap);

  if (!record) return null;

  return (
    <div className="dbom-ct-drawer-info">
      {/* Game */}
      <div className="dbom-ct-drawer-info__row">
        <div className="dbom-ct-drawer-info__label">{intl('Game')}</div>
        <div className="dbom-ct-drawer-info__value">
          <RenderGameIcon
            className="dbom-ct-drawer-info__game-logo"
            gameInfo={{
              cover: gameLogo,
              entity_type: (record as any)?.entity_type,
              edition_unified_id: record.game_id,
            }}
          />
          <span className="dbom-ct-drawer-info__game-name">{gameName}</span>
        </div>
      </div>

      {/* KPI 行（由 kpiConfigs 驱动） */}
      {kpiConfigs.map(({ dataKey, labelKey, decimal }) => (
        <TrendInfoKpiRow
          key={dataKey}
          label={intl(labelKey)}
          kpiData={(record as any)?.[dataKey]}
          formatValue={
            decimal !== undefined
              ? (v: number) => DBO_V.reviews(v, decimal)
              : undefined
          }
        />
      ))}

      {/* Type & Elements */}
      <div className="dbom-ct-drawer-info__row">
        <div className="dbom-ct-drawer-info__label">
          {intl('Type & Elements')}
        </div>
        <div className="dbom-ct-drawer-info__value dbom-ct-drawer-info__value--chips">
          <TrendTopicTags
            record={record}
            topicTypeColorMap={topicTypeColorMap}
            chipClassName="dbom-ct-drawer-info__chip"
          />
        </div>
      </div>
    </div>
  );
}
